import React from 'react';
import { RawHtml } from '@unisporkal/utilities';
import { useTranslations } from '@unisporkal/localization';
import { useSiteMap } from '@unisporkal/sites';
import {
  Link,
  ListItem,
  Spinner,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import PrinterIcon from '../../../icons/Printer.svg?icon';
import BadgeIcon from '../../../icons/Badge.svg?icon';
import ReportIcon from '../../../icons/Report.svg?icon';
import styles from './ModifiedDownloadInProgress.module.scss';

interface ModifiedDownloadInProgressProps {
  showLicenseBenefits?: boolean;
}

const ModifiedDownloadInProgress: React.FC<ModifiedDownloadInProgressProps> = ({
  showLicenseBenefits = false,
}) => {
  const t = useTranslations();
  const siteMap = useSiteMap();

  return (
    <section
      className={styles.container}
      data-testid="modified-download-in-progress"
    >
      <Typography
        className={styles.header}
        component={TypographyComponentEnum.H5}
        variant={TypographyVariant.body.large}
      >
        {t('download_in_progress')}
      </Typography>
      <Typography
        className={styles.subhead}
        component={TypographyComponentEnum.P}
        variant={TypographyVariant.body.small}
      >
        {t('this_may_take_a_moment')}
      </Typography>
      <Spinner
        className={styles.spinner}
        data-testid="loading-spinner"
      />
      <Typography
        className={styles.body}
        component={TypographyComponentEnum.P}
        variant={TypographyVariant.body.small}
      >
        {t('experiencing_issues_with_download')}
      </Typography>

      {showLicenseBenefits && (
        <section
          className={styles.benefits}
          data-testid="license-benefits"
        >
          <hr className={styles.divider} />
          <Typography
            className={styles.benefitsHeader}
            component={TypographyComponentEnum.H6}
            variant={TypographyVariant.body.small}
          >
            {t('gi_rf_includes')}
          </Typography>
          <ul className={styles.list}>
            <ListItem className={styles.listItem}>
              <PrinterIcon />
              {t('unlimited_print_runs')}
            </ListItem>
            <ListItem className={styles.listItem}>
              <BadgeIcon className={styles.badge} />
              {t('comprehensive_protection')}
            </ListItem>
            <ListItem className={styles.listItem}>
              <ReportIcon className={styles.report} />
              <RawHtml>
                {t('straightfoward_discounts', {
                  links: [
                    {
                      href: siteMap.plansAndPricing(),
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    },
                  ],
                })}
              </RawHtml>
            </ListItem>
          </ul>
          <Link
            className={styles.link}
            href={siteMap.eula()}
            target="_blank"
            data-testid="view-license-agreement"
            rel="noopener noreferrer"
          >
            {t('view_full_agreement')}
          </Link>
        </section>
      )}
    </section>
  );
};

export default ModifiedDownloadInProgress;
